import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import {
  ShieldCheck, ShieldAlert, ShieldX, Upload, FileUp, Loader2,
  CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronUp,
  Wrench, Fuel, Gauge, Activity, Heart, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  green: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-500/20', label: 'PASS' },
  yellow: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-500/20', label: 'WARN' },
  red: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-500/20', label: 'FAIL' },
};

const CATEGORY_ICONS = {
  'Idle Quality': Gauge,
  'Fuel System': Fuel,
  'Under Load': Zap,
  'Engine Health': Heart,
  'Performance': Activity,
  'Data Quality': FileUp,
};

function FileDropZone({ label, description, file, onFile, accept = ".csv" }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  return (
    <div
      onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer?.files?.[0]) onFile(e.dataTransfer.files[0]); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onClick={() => inputRef.current?.click()}
      className={`relative border-2 border-dashed rounded-sm p-5 text-center cursor-pointer transition-all ${
        file ? 'border-emerald-500/30 bg-emerald-500/5' :
        dragging ? 'border-[#FF5E1A] bg-[#FF5E1A]/5' :
        'border-[#27272A] hover:border-zinc-600 bg-[#0A0A0A]'
      }`}
    >
      <input ref={inputRef} type="file" accept={accept} onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }} className="hidden" />
      {file ? (
        <div className="flex items-center gap-2 justify-center">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
          <span className="text-xs text-emerald-400 font-['JetBrains_Mono']">{file.name}</span>
        </div>
      ) : (
        <>
          <Upload className={`w-6 h-6 mx-auto mb-2 ${dragging ? 'text-[#FF5E1A]' : 'text-zinc-600'}`} />
          <p className="text-xs text-zinc-300 font-bold">{label}</p>
          <p className="text-[10px] text-zinc-600 mt-0.5">{description}</p>
        </>
      )}
    </div>
  );
}

function CheckCard({ check, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const config = STATUS_CONFIG[check.status];
  const Icon = config.icon;
  const CategoryIcon = CATEGORY_ICONS[check.category] || Activity;

  return (
    <div className={`border ${config.border} rounded-sm overflow-hidden`} data-testid={`health-check-${check.name.toLowerCase().replace(/\s+/g, '-')}`}>
      <button onClick={() => setExpanded(!expanded)} className={`w-full flex items-center gap-3 p-3 text-left ${config.bg} hover:brightness-110 transition-all`}>
        <Icon className={`w-4 h-4 ${config.color} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <CategoryIcon className="w-3 h-3 text-zinc-500" />
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{check.category}</span>
          </div>
          <p className="text-sm font-bold text-white font-['Outfit']">{check.name}</p>
        </div>
        <Badge className={`${config.bg} ${config.color} text-[9px] px-1.5 py-0 rounded-sm border ${config.border} flex-shrink-0`}>
          {config.label}
        </Badge>
        <span className="text-xs text-zinc-400 font-['JetBrains_Mono'] flex-shrink-0 max-w-[140px] truncate hidden sm:block">{check.value}</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
      </button>
      {expanded && (
        <div className="p-4 bg-[#0A0A0A] space-y-3">
          <p className="text-xs text-zinc-400 font-['JetBrains_Mono']">{check.value}</p>
          <p className="text-sm text-zinc-300 leading-relaxed">{check.explanation}</p>
          {check.fix && (
            <div className="flex items-start gap-2 p-3 bg-[#111111] border border-[#27272A] rounded-sm">
              <Wrench className="w-3.5 h-3.5 text-[#FF5E1A] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-[#FF5E1A] uppercase tracking-wider font-bold mb-1">Recommended Fix</p>
                <p className="text-xs text-zinc-300">{check.fix}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HealthCheck({ onProceed }) {
  const [idleFile, setIdleFile] = useState(null);
  const [wotFile, setWotFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const runCheck = useCallback(async () => {
    if (!idleFile || !wotFile) return;
    setRunning(true);
    setError(null);
    setReport(null);
    try {
      const formData = new FormData();
      formData.append('idle_log', idleFile);
      formData.append('wot_log', wotFile);

      const res = await fetch(`${API_URL}/api/health-check`, { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Health check failed');
      }
      setReport(await res.json());
    } catch (err) {
      setError(err.message);
    }
    setRunning(false);
  }, [idleFile, wotFile]);

  const OverallIcon = report ? (report.overall_status === 'green' ? ShieldCheck : report.overall_status === 'yellow' ? ShieldAlert : ShieldX) : ShieldCheck;
  const overallConfig = report ? STATUS_CONFIG[report.overall_status] : null;

  return (
    <div className="space-y-5" data-testid="health-check">
      {/* Info banner */}
      <Alert className="bg-[#FF5E1A]/5 border-[#FF5E1A]/20 rounded-sm">
        <ShieldCheck className="w-4 h-4 text-[#FF5E1A]" />
        <AlertTitle className="text-[#FF5E1A] font-['Outfit'] text-sm font-bold">Mechanical Health Check — Fix Problems First</AlertTitle>
        <AlertDescription className="text-zinc-400 text-xs leading-relaxed mt-1">
          Your idle and 3rd gear logs help us detect vacuum leaks, sensor failures, knock, and other issues <strong className="text-white">before</strong> we start tuning. Addressing these now prevents costly engine damage and ensures the AI can tune effectively.
        </AlertDescription>
      </Alert>

      {/* Upload Section */}
      {!report && (
        <Card className="bg-[#111111] border-[#27272A] rounded-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
              <Upload className="w-5 h-5 text-[#FF5E1A]" />Upload Datalogs for Health Check
            </CardTitle>
            <CardDescription className="text-zinc-500 text-xs">
              Upload two CSV datalogs: one at idle (engine warm, 2+ min) and one with a 3rd gear WOT pull
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <FileDropZone
                label="Idle Datalog"
                description="Engine warm, parked, 2+ min idle"
                file={idleFile}
                onFile={setIdleFile}
              />
              <FileDropZone
                label="WOT Pull Datalog"
                description="3rd gear, 2500 RPM to redline"
                file={wotFile}
                onFile={setWotFile}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-sm mb-4">
                <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            <Button
              onClick={runCheck}
              disabled={!idleFile || !wotFile || running}
              className="w-full bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm py-3 font-bold text-sm uppercase tracking-wider"
              data-testid="run-health-check-btn"
            >
              {running ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyzing Engine Health...</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" />Run Health Check</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Report */}
      {report && (
        <>
          {/* Overall Status */}
          <Card className={`border ${overallConfig.border} rounded-sm ${overallConfig.bg}`}>
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-sm ${overallConfig.bg} border ${overallConfig.border}`}>
                    <OverallIcon className={`w-7 h-7 ${overallConfig.color}`} />
                  </div>
                  <div>
                    <p className={`text-lg font-bold font-['Outfit'] ${overallConfig.color}`}>
                      {report.overall_status === 'green' ? 'Engine Healthy' : report.overall_status === 'yellow' ? 'Caution — Issues Detected' : 'Stop — Critical Issues Found'}
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">{report.safety_message}</p>
                  </div>
                </div>
                <div className="flex gap-3 text-center">
                  <div><p className="text-lg font-bold text-emerald-400">{report.summary.green}</p><p className="text-[9px] text-zinc-500">PASS</p></div>
                  <div><p className="text-lg font-bold text-amber-400">{report.summary.yellow}</p><p className="text-[9px] text-zinc-500">WARN</p></div>
                  <div><p className="text-lg font-bold text-red-400">{report.summary.red}</p><p className="text-[9px] text-zinc-500">FAIL</p></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Individual Checks */}
          <div className="space-y-2">
            {report.checks.map((check, i) => (
              <CheckCard key={`${check.name}-${i}`} check={check} defaultExpanded={check.status === 'red'} />
            ))}
          </div>

          {/* Liability */}
          <div className="p-4 bg-[#0A0A0A] border border-yellow-500/15 rounded-sm">
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-yellow-500/70 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                <span className="text-yellow-500/80 font-bold">Disclaimer:</span> AI Tuner Pro is an experimental assistant only. We flag potential mechanical issues but cannot diagnose every problem. Always verify on a dyno with proper monitoring. You are fully responsible for your vehicle's safety and any modifications.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={() => { setReport(null); setIdleFile(null); setWotFile(null); }}
              variant="outline" className="border-zinc-700 text-zinc-400 rounded-sm">
              Re-Upload Logs
            </Button>
            {report.can_proceed && onProceed && (
              <Button onClick={onProceed}
                className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm flex-1"
                data-testid="proceed-to-tuning-btn">
                <Zap className="w-4 h-4 mr-2" />Proceed to Tuning
              </Button>
            )}
            {!report.can_proceed && (
              <div className="flex-1 flex items-center justify-center p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
                <p className="text-xs text-red-400 font-bold">Resolve red issues before tuning</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
